/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.browser.composable

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import systems.frictionless.impulse.browser.composable.component.ButtonDescription
import systems.frictionless.impulse.browser.composable.component.ContentContainer
import systems.frictionless.impulse.browser.composable.component.LoadingIndicator
import systems.frictionless.impulse.browser.composable.component.SectionIntro
import systems.frictionless.impulse.browser.composable.component.SideNavigationSection
import systems.frictionless.impulse.browser.composable.component.SurveyProgressBar
import systems.frictionless.impulse.browser.composable.question.Question
import systems.frictionless.impulse.browser.id.HtmlId
import systems.frictionless.impulse.browser.style.AppStyleSheet
import systems.frictionless.impulse.rest.client.model.Progress
import systems.frictionless.impulse.rest.model.Answer
import systems.frictionless.impulse.viewmodel.DemographicsViewModel

@Composable
fun DemographicsSection(
    demographicsViewModel: DemographicsViewModel,
    onDemographicsComplete: () -> Unit
) {
    when (val demographicsStateSnapshot = demographicsViewModel.state.collectAsState().value) {
        is DemographicsViewModel.State.Overview -> DemographicsOverview(demographicsStateSnapshot)
        is DemographicsViewModel.State.Questions -> DemographicsQuestions(demographicsStateSnapshot)
        is DemographicsViewModel.State.Complete -> onDemographicsComplete()
    }
}

@Composable
private fun DemographicsOverview(overviewState: DemographicsViewModel.State.Overview) {
    ContentContainer(
        navigationSection = SideNavigationSection.Demographics,
        mainContainerCssClass = AppStyleSheet.demographics,
        mainContentContainerId = HtmlId.SectionDemographicsOverview,
        mainContent = {
            SectionIntro(
                "Demographics",
                "Let's start with a few demographic questions. The responses will help adapt the assessment for different types of work and workplaces."
            ) {
                overviewState.next()
            }
        }
    )
}

@Composable
private fun DemographicsQuestions(questionState: DemographicsViewModel.State.Questions) {
    val progress = remember { mutableStateOf(Progress.EMPTY) }
    val answer = remember { mutableStateOf<Answer.Builder?>(null) }

    val composableScope = rememberCoroutineScope()

    ContentContainer(
        navigationSection = SideNavigationSection.Demographics,
        mainContainerCssClass = AppStyleSheet.demographics,
        mainContentContainerId = HtmlId.SectionDemographicsQuestions,
        progressArea = {
            // Disable button while loading, to make the button feel more responsive.
            val isIdle =
                questionState.status.collectAsState().value is DemographicsViewModel.State.Questions.Status.Idle

            SurveyProgressBar(
                progress = progress.value,
                nextButton =
                    ButtonDescription(
                        label = "Next",
                        action = {
                            questionState.answerQuestion(
                                composableScope,
                                requireNotNull(answer.value) {
                                    "You should not be able to call action when answer value is null"
                                }
                            )
                        },
                        enabled = null != answer.value && isIdle
                    )
            )
        },
        mainContent = {
            when (questionState.status.collectAsState().value) {
                is DemographicsViewModel.State.Questions.Status.Idle -> {
                    val questionWithProgress = questionState.questionWithProgress.collectAsState().value
                    if (null == questionWithProgress) {
                        questionState.fetchNextQuestion(composableScope)
                        LoadingIndicator()
                    } else {
                        Question(
                            questionState.assessment,
                            questionWithProgress.question,
                            questionWithProgress.progress,
                            showExtraInputFields = true
                        ) { answerBuilder ->
                            answer.value = answerBuilder
                        }
                        progress.value = questionWithProgress.progress
                    }
                }
                is DemographicsViewModel.State.Questions.Status.Busy -> {
                    LoadingIndicator()
                }
            }
        }
    )
}
